import { useEffect, useMemo, useRef, useState } from "react";
import {
  arrayOf as arrayType,
  params, shape,
  string as stringType,
  useLazyDataModel
} from "@thd-nucleus/data-sources";

export const useProductsBatched = ({ materials = []}) => {
  const [piTriggered, setPiTriggered] = useState(false);
  const BATCH_SIZE = 16;

  const itemsWithPI = useMemo(() => {
    const materialItems = materials.flatMap(group => group.items || [])
      .filter(item => item?.itemId && item?.name);
    const piMap = {};
    materialItems.forEach(item => {
      piMap[item.itemId] = {
        name: item.name,
        brand: item.brand
      };
    });
    return piMap;
  },[materials]);

  const [itemIdsPiMap, setItemIdsPiMap] = useState(itemsWithPI);

  // Update existingMaterials if materials prop changes
  const itemIdsMissingPI = useMemo(() => {
    const materialItems = materials.flatMap(group => group.items || [])
      .slice(0, 96); // Limit to first 96 itemIds
    return materialItems
      .filter(item => item?.itemId && !item?.name)
      .map(item => item.itemId)
  }, [materials]);

  const itemIds = useMemo(
    () => {
      if (Object.keys(itemIdsPiMap).length === 0) return itemIdsMissingPI;
      return itemIdsMissingPI.filter(id => !itemIdsPiMap[id])
    }
  , [itemIdsMissingPI, itemIdsPiMap]);

  const [ fetchProducts, { data, error, loading } ] = useLazyDataModel('products',{
    variables: {}
  });
  const { products } = data || {};

  const newItemIdsMap = useMemo(() => {
    const __newItemIdsMap = {};
    if (products?.length > 0) {
      products.forEach((product) => {
        __newItemIdsMap[product.itemId] = {
          name: product.identifiers.productLabel,
          brand: product.identifiers.brandName
        };
      });
    }
    return __newItemIdsMap;
  }, [products]);

  useEffect(() => {
    if (Object.keys(newItemIdsMap).length > 0) {
      setItemIdsPiMap(prevMap => ({ ...prevMap, ...newItemIdsMap }));
    }
  }, [newItemIdsMap]);

  useEffect(() => {
    if (itemIds.length > 0 && piTriggered) {
      const currentBatch = itemIds.slice(0, BATCH_SIZE);
      fetchProducts({
        variables: {
          itemIds: currentBatch
        }
      });
    }
  }, [itemIds, piTriggered]);

  return {
    itemIdsMap: itemIdsPiMap,
    isPILoading: loading || itemIds.length > BATCH_SIZE,
    piTriggered,
    setPiTriggered
  }
}

useProductsBatched.dataModel ={
  products: params({
    itemIds: arrayType(stringType().isRequired()).isRequired(),
    dataSource: stringType(),
  }).arrayOf(shape({
    itemId: stringType(),
    identifiers: shape({
      brandName: stringType(),
      productLabel: stringType()
    }),
  })),
}
